import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaLinkedin,
  FaTwitter,
  FaGithub,
  FaEnvelope,
  FaRocket,
  FaUsers,
  FaAward,
  FaLightbulb,
} from "react-icons/fa";

const TeamSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0a0a0a 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(102, 126, 234, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(118, 75, 162, 0.03) 0%,
      transparent 50%
    );
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.3);
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 50%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #b8b8b8;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const TeamCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 25px;
  padding: 40px;
  text-align: center;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-10px);
    border-color: rgba(102, 126, 234, 0.3);
    box-shadow: 0 20px 40px rgba(102, 126, 234, 0.1);
  }
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  margin: 0 auto 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  ${TeamCard}:hover &::before {
    left: 100%;
  }
`;

const MemberName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 10px;
`;

const MemberRole = styled.div`
  color: #667eea;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 15px;
`;

const MemberDescription = styled.p`
  color: #b8b8b8;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b8b8b8;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transform: translateY(-2px);
  }
`;

const StatsSection = styled.div`
  margin-top: 80px;
`;

const StatsTitle = styled(motion.h3)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 50px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #667eea;
  margin-bottom: 15px;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #b8b8b8;
`;

const Team = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const teamMembers = [
    {
      name: "张总",
      role: "创始人 & CEO",
      description:
        "10年数字营销经验，曾任职于知名互联网公司，专注于企业数字化转型和品牌营销策略制定。",
      avatar: "👨‍💼",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "kun@ersansan.cn",
      },
    },
    {
      name: "李经理",
      role: "技术总监",
      description:
        "资深技术专家，专注于广告投放技术优化和数据分析系统开发，拥有丰富的技术架构经验。",
      avatar: "👨‍💻",
      social: {
        linkedin: "#",
        github: "#",
        email: "tech@ersansan.cn",
      },
    },
    {
      name: "王总监",
      role: "创意总监",
      description:
        "资深创意设计师，专注于品牌视觉设计和创意内容制作，为众多知名品牌提供创意解决方案。",
      avatar: "👩‍🎨",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "creative@ersansan.cn",
      },
    },
    {
      name: "陈经理",
      role: "运营总监",
      description:
        "数字营销运营专家，专注于社交媒体运营和用户增长，拥有丰富的运营策略制定经验。",
      avatar: "👩‍💼",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "ops@ersansan.cn",
      },
    },
  ];

  const teamStats = [
    {
      icon: <FaUsers />,
      number: "50+",
      label: "专业团队成员",
    },
    {
      icon: <FaAward />,
      number: "15+",
      label: "行业认证证书",
    },
    {
      icon: <FaRocket />,
      number: "8+",
      label: "平均行业经验",
    },
    {
      icon: <FaLightbulb />,
      number: "100+",
      label: "创新解决方案",
    },
  ];

  return (
    <TeamSection id="team">
      <BackgroundPattern />
      <Container ref={ref}>
        <SectionHeader>
          <SectionBadge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <FaUsers />
            专业团队
          </SectionBadge>

          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            我们的专业团队
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            由经验丰富的专业人士组成，致力于为客户提供最优质的服务
          </SectionSubtitle>
        </SectionHeader>

        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamCard
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <Avatar>{member.avatar}</Avatar>

              <MemberName>{member.name}</MemberName>
              <MemberRole>{member.role}</MemberRole>
              <MemberDescription>{member.description}</MemberDescription>

              <SocialLinks>
                {member.social.linkedin && (
                  <SocialLink
                    href={member.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaLinkedin />
                  </SocialLink>
                )}
                {member.social.twitter && (
                  <SocialLink
                    href={member.social.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaTwitter />
                  </SocialLink>
                )}
                {member.social.github && (
                  <SocialLink
                    href={member.social.github}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaGithub />
                  </SocialLink>
                )}
                {member.social.email && (
                  <SocialLink href={`mailto:${member.social.email}`}>
                    <FaEnvelope />
                  </SocialLink>
                )}
              </SocialLinks>
            </TeamCard>
          ))}
        </TeamGrid>

        <StatsSection>
          <StatsTitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            团队实力
          </StatsTitle>

          <StatsGrid>
            {teamStats.map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.8, delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <StatIcon>{stat.icon}</StatIcon>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </StatsSection>
      </Container>
    </TeamSection>
  );
};

export default Team;
